import axios from 'axios';
import apiClient from './index';
import { API_CONFIG } from '../config/api';
import { taskManagerClient } from '../utils/taskManager';

// 类型守卫函数
const isValidTaskResult = (result: any): boolean => {
  return result && typeof result === 'object' && result.status === 'COMPLETED';
};

const isValidUnifiedResult = (result: any): boolean => {
  return result && typeof result === 'object' && Array.isArray(result.results);
};

const safeExtractResults = (result: any, fallback: any[] = []): any[] => {
  if (!isValidUnifiedResult(result)) {
    console.warn('[ADAPTER] Invalid unified result format, using fallback:', {
      hasResult: !!result,
      resultType: typeof result,
      hasResultsArray: Array.isArray(result?.results)
    });
    return fallback;
  }
  
  try {
    const extracted = result.results?.map((item: any, index: number) => {
      if (item && typeof item === 'object' && item.result) {
        console.log(`[ADAPTER] 提取结果项 ${index}:`, {
          hasResult: !!item.result,
          resultType: typeof item.result,
          resultSize: JSON.stringify(item.result).length
        });
        return item.result;
      }
      console.warn(`[ADAPTER] Invalid result item ${index} format:`, item);
      return item;
    }) || fallback;
    
    console.log(`[ADAPTER] 成功提取 ${extracted.length} 个结果项`);
    return extracted;
  } catch (error) {
    console.error('[ADAPTER] Error extracting results:', error);
    return fallback;
  }
};

// 轮询任务结果的函数
export const pollTaskResult = async (taskId: string, maxAttempts: number = 30, intervalMs: number = 2000): Promise<any> => {
  const startTime = Date.now();
  console.log(`[POLLING] 开始轮询任务 ${taskId}，最大尝试次数: ${maxAttempts}，间隔: ${intervalMs}ms`);
  
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      // 使用公开的状态接口，不需要认证
      const response = await fetch(`${API_CONFIG.TASK_MANAGER_BASE}/tasks/${taskId}/status`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const task = await response.json();
      const elapsed = Date.now() - startTime;
      console.log(`[POLLING] 第 ${attempt + 1}/${maxAttempts} 次，耗时: ${elapsed}ms，任务状态:`, {
        id: task.id,
        status: task.status,
        progress: task.progress,
        hasResult: !!task.result,
        resultKeys: task.result ? Object.keys(task.result) : [],
        resultSize: task.result ? JSON.stringify(task.result).length : 0
      });
      
      // 检查任务是否完成 - 支持多种状态格式
      if ((task.status === 'COMPLETED' || task.status === 'completed') && task.result) {
        const totalTime = Date.now() - startTime;
        console.log(`[POLLING] 任务 ${taskId} 完成，总耗时: ${totalTime}ms，尝试次数: ${attempt + 1}`);
        console.log(`[RESULT] 结果数据结构:`, {
          hasResults: Array.isArray(task.result.results),
          resultsCount: task.result.results?.length || 0,
          hasMeta: !!task.result.meta,
          hasArtifacts: !!task.result.artifacts
        });
        return task.result;
      } else if (task.status === 'failed' || task.status === 'FAILED') {
        console.error('任务失败:', task.error);
        throw new Error(task.error || '任务执行失败');
      }
      
      // 等待后继续轮询
      if (attempt < maxAttempts - 1) {
        console.log(`等待 ${intervalMs}ms 后继续轮询...`);
        await new Promise(resolve => setTimeout(resolve, intervalMs));
      }
    } catch (error) {
      const elapsed = Date.now() - startTime;
      console.error(`[POLLING] 第 ${attempt + 1}/${maxAttempts} 次失败，耗时: ${elapsed}ms:`, error);
      if (attempt === maxAttempts - 1) {
        console.error(`[POLLING] 任务 ${taskId} 最终失败，总耗时: ${elapsed}ms`);
        throw new Error(`任务 ${taskId} 轮询失败: ${error}`);
      }
    }
    
    // 等待后继续轮询
    if (attempt < maxAttempts - 1) {
      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }
  }
  
  const totalTime = Date.now() - startTime;
  console.error(`[POLLING] 任务 ${taskId} 超时，总耗时: ${totalTime}ms，尝试次数: ${maxAttempts}`);
  throw new Error(`任务 ${taskId} 在 ${maxAttempts} 次尝试后仍未完成`);
};

// RNAhybrid类型定义
export interface RNAHybridRequest {
  mirna_sequence: string;
  target_sequence: string;
  species?: '3utr_human' | '3utr_fly' | '3utr_worm';
  hits_per_target?: number;
  max_target_length?: number;
}

export interface RNAHybridResult {
  mirna_id: string;
  target_id: string;
  mfe: number;
  position: number;
  raw_output: string;  // 原始输出字段
}

export interface RNAHybridResponse {
  results: RNAHybridResult[];
  command: string;
  runtime: number;
}

// PRODIGY类型定义
export interface ProdigyRequest {
  temperature?: number;
  distance_cutoff?: number;
  selections?: string;
}

export interface StructureInfo {
  filename: string;
  chains: string[];
  size: number;
}

export interface ProdigyResult {
  binding_affinity: number;
  dissociation_constant: string;
  intermolecular_contacts: number;
  charged_charged_contacts: number;
  charged_polar_contacts: number;
  charged_apolar_contacts: number;
  polar_polar_contacts: number;
  apolar_polar_contacts: number;
  apolar_apolar_contacts: number;
  apolar_nis_percentage: number;
  charged_nis_percentage: number;
  chains: string[];
  used_selection?: string;
  command: string;
  raw_output: string;
}

export interface ProdigyResponse {
  results: ProdigyResult[];
  structure_info: {
    filename: string;
    chains: string[];
    size: number;
  };
  runtime: number;
}

// ZDOCK类型定义
export interface ZdockPrediction {
  rank: number;
  score: number;
  rotation: number[];
  translation: number[];
  pdb_file: string;
  pdb_content: string; 
}

export interface ZdockResponse {
  predictions: ZdockPrediction[];
  receptor_info: {
    filename: string;
    size: number;
  };
  ligand_info: {
    filename: string;
    size: number;
  };
  command: string;
  runtime: number;
  output_dir: string;
  raw_output: string;
}

/**
 * 格式化API错误信息
 * @param error 捕获的错误对象
 * @returns 格式化后的错误消息
 */
const formatError = (error: any): Error => {
  let message = '未知错误';
  
  if (axios.isAxiosError(error)) {
    // Axios错误
    if (error.response) {
      // 服务器返回了错误状态码
      const detail = error.response.data?.detail;
      message = detail || `服务器错误: ${error.response.status}`;
    } else if (error.request) {
      // 请求已发送但没有收到响应
      message = '服务器无响应，请检查网络连接';
    } else {
      // 请求配置有问题
      message = `请求错误: ${error.message}`;
    }
  } else if (error instanceof Error) {
    message = error.message;
  }
  
  const formattedError = new Error(message);
  formattedError.name = 'ApiError';
  
  return formattedError;
};

/**
 * 执行分子互作分析任务的通用函数
 * @param taskId 任务ID
 * @param data 任务数据
 * @returns 任务结果
 */
export const executeInteractionTask = async (taskId: string, data: any): Promise<any> => {
  try {
    console.log(`执行分子互作任务: ${taskId}`, data);

    // 映射任务ID到handler名称
    const taskIdToHandler: { [key: string]: { handler: string; taskType: string } } = {
      'rnahybrid': { handler: 'rnahybrid_predict_json', taskType: 'rnahybrid' },
      'rnahybrid_predict': { handler: 'rnahybrid_predict_json', taskType: 'rnahybrid' },
      'prodigy': { handler: 'prodigy_analyze_json', taskType: 'prodigy' },
      'prodigy_analyze': { handler: 'prodigy_analyze_json', taskType: 'prodigy' },
      'zdock': { handler: 'zdock_dock_json', taskType: 'zdock' },
      'zdock_dock': { handler: 'zdock_dock_json', taskType: 'zdock' },
      'megadock': { handler: 'megadock_dock_json', taskType: 'megadock' },
      'equidock': { handler: 'equidock_dock_json', taskType: 'equidock' }
    };

    let normalizedId = taskId.replace(/^.*\//, '').toLowerCase();
    const mapping = taskIdToHandler[normalizedId]
      || taskIdToHandler[`${normalizedId}_predict`]
      || { handler: 'rnahybrid_predict_json', taskType: 'rnahybrid' };

    const taskData = {
      name: `分子互作任务: ${taskId}`,
      description: `执行${taskId}分子互作分析`,
      service_name: 'molecular_interaction',
      task_type: mapping.taskType,
      handler_name: mapping.handler,
      parameters: data,
      dependencies: [],
      priority: 5,
      timeout: 3600
    };

    // 使用taskManagerClient创建任务，确保用户认证上下文正确传递
    const taskResponse = await taskManagerClient.createTask(taskData);
    const taskId_created = taskResponse.id;

    // 轮询任务结果
    const result = await pollTaskResult(taskId_created);
    
    return result;
  } catch (error) {
    console.error(`分子互作任务 ${taskId} 执行失败:`, error);
    throw formatError(error);
  }
};

/**
 * 分子互作分析服务API客户端
 */
const molecularInteractionService = {
  /**
   * 运行RNAhybrid预测miRNA与mRNA靶标结合位点 - 通过任务管理器
   * @param request RNAhybrid请求参数
   * @returns RNAhybrid分析结果
   */
  runRNAhybrid: async (request: RNAHybridRequest): Promise<RNAHybridResponse> => {
    try {
      const result = await executeInteractionTask('rnahybrid', {
        mirna_sequence: request.mirna_sequence,
        target_sequence: request.target_sequence,
        species: request.species || '3utr_human',
        hits_per_target: request.hits_per_target || 2,
        max_target_length: request.max_target_length || 100
      });
      
      console.log('[RNAHYBRID] 统一格式化结果:', {
        hasResults: Array.isArray(result.results),
        resultsCount: result.results?.length || 0,
        hasMeta: !!result.meta
      });
      
      // 从统一格式化结果中提取原始RNAhybrid数据，使用类型守卫
      const rnahybridResults = safeExtractResults(result, []);
      console.log('[RNAHYBRID] 提取的结果:', {
        count: rnahybridResults.length,
        firstResultType: rnahybridResults[0] ? typeof rnahybridResults[0] : 'none'
      });
      
      // 返回前端组件期望的格式，带有严格的类型检查
      return {
        results: rnahybridResults,
        command: (result.meta && typeof result.meta.command === 'string') ? result.meta.command : '',
        runtime: (result.meta && typeof result.meta.runtime === 'number') ? result.meta.runtime : 0
      };
    } catch (error) {
      console.error('RNAhybrid请求失败:', error);
      throw formatError(error);
    }
  },

  /**
   * 解析结构文件获取链信息 - 保持直接调用
   * @param file 结构文件
   * @returns 结构文件信息
   */
  parseStructure: async (file: File): Promise<StructureInfo> => {
    try {
      console.log('开始解析结构文件:', file.name, file.size, file.type);
      
      // 创建FormData对象
      const formData = new FormData();
      formData.append('structure_file', file);
      
      // 输出完整请求路径
      const requestUrl = `/molecular_interaction/prodigy_parse_structure`;
      console.log('发送请求到:', requestUrl);
      
      const response = await apiClient.post(requestUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      console.log('结构解析响应:', response);
      
      // 确保返回的数据符合StructureInfo类型
      const responseData = response.data || response;
      if (responseData && typeof responseData === 'object') {
        console.log('解析到链信息:', responseData.chains);
        return responseData as StructureInfo;
      }
      
      // 如果格式不正确，返回一个默认值
      console.warn('API返回数据格式不符合预期，使用默认值');
      return {
        filename: file.name,
        chains: [],
        size: file.size
      };
    } catch (error: any) {
      console.error('解析结构文件失败:', error);
      // 打印出更详细的错误信息
      if (error.response) {
        console.error('错误响应:', error.response.status, error.response.data);
      }
      throw formatError(error);
    }
  },

  /**
   * 运行PRODIGY预测蛋白质-蛋白质互作 - 通过任务管理器
   * @param file 结构文件
   * @param params PRODIGY分析参数
   * @returns PRODIGY分析结果
   */
  runProdigy: async (file: File, params: ProdigyRequest): Promise<ProdigyResponse> => {
    try {
      console.log('通过任务管理器执行PRODIGY分析...', params);
      
      // 将文件转换为文本内容
      const fileContent = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result);
        };
        reader.onerror = reject;
        reader.readAsText(file);
      });
      
      const result = await executeInteractionTask('prodigy', {
        structure_content: fileContent,
        filename: file.name,
        temperature: params.temperature || 25.0,
        distance_cutoff: params.distance_cutoff || 5.5,
        selections: params.selections || ''
      });
      
      // 适配数据格式：从统一格式中提取Prodigy结果，使用类型守卫
      const prodigyResults = safeExtractResults(result, []);
      
      // 安全提取结构信息
      const structureInfo = (() => {
        if (result.artifacts && typeof result.artifacts === 'object') {
          return {
            filename: result.artifacts.filename || file.name,
            chains: Array.isArray(result.artifacts.chains) ? result.artifacts.chains : [],
            size: typeof result.artifacts.size === 'number' ? result.artifacts.size : file.size
          };
        }
        return {
          filename: file.name,
          chains: [],
          size: file.size
        };
      })();
      
      return {
        results: prodigyResults,
        structure_info: structureInfo,
        runtime: (result.meta && typeof result.meta.runtime === 'number') ? result.meta.runtime : 0
      };
    } catch (error) {
      console.error('PRODIGY请求失败:', error);
      throw formatError(error);
    }
  },

  /**
   * 运行ZDOCK进行分子对接 - 通过任务管理器
   * @param receptorFile 受体蛋白PDB文件
   * @param ligandFile 配体蛋白PDB文件
   * @param numPredictions 预测结构数量
   * @returns ZDOCK分析结果
   */
  runZdock: async (
    receptorFile: File, 
    ligandFile: File, 
    numPredictions: number = 100
  ): Promise<ZdockResponse> => {
    try {
      console.log('通过任务管理器执行ZDOCK分析...');
      
      // 将文件转换为文本内容
      const receptorContent = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result);
        };
        reader.onerror = reject;
        reader.readAsText(receptorFile);
      });
      
      const ligandContent = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result);
        };
        reader.onerror = reject;
        reader.readAsText(ligandFile);
      });
      
      const result = await executeInteractionTask('zdock', {
        receptor_content: receptorContent,
        ligand_content: ligandContent,
        receptor_filename: receptorFile.name,
        ligand_filename: ligandFile.name,
        num_predictions: numPredictions,
        spacing: 1.2,
        dense_sampling: false,
        fix_receptor: false
      });
      
      // 适配数据格式：从统一格式中提取ZDOCK结果，使用类型守卫
      const zdockResults = safeExtractResults(result, []);
      
      // 安全提取受体信息
      const receptorInfo = (() => {
        const info = result.artifacts?.receptor_info || result.receptor_info;
        if (info && typeof info === 'object') {
          return {
            filename: info.filename || receptorFile.name,
            size: typeof info.size === 'number' ? info.size : receptorFile.size
          };
        }
        return {
          filename: receptorFile.name,
          size: receptorFile.size
        };
      })();
      
      // 安全提取配体信息
      const ligandInfo = (() => {
        const info = result.artifacts?.ligand_info || result.ligand_info;
        if (info && typeof info === 'object') {
          return {
            filename: info.filename || ligandFile.name,
            size: typeof info.size === 'number' ? info.size : ligandFile.size
          };
        }
        return {
          filename: ligandFile.name,
          size: ligandFile.size
        };
      })();
      
      return {
        predictions: Array.isArray(result.predictions) ? result.predictions : zdockResults,
        receptor_info: receptorInfo,
        ligand_info: ligandInfo,
        command: (result.meta?.command || result.command || '').toString(),
        runtime: (result.meta?.runtime || result.runtime || 0),
        output_dir: (result.output_dir || '').toString(),
        raw_output: (result.raw_output || '').toString()
      };
    } catch (error) {
      console.error('ZDOCK请求失败:', error);
      throw formatError(error);
    }
  }
};

export default molecularInteractionService;